import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { needsSignupResume } from '../utils/needsSignupResume';

/**
 * Component to handle role-based redirects on page load/refresh
 * Checks if authenticated user is on the correct page based on their role
 * Only redirects users who are on invalid routes, not those already on valid routes
 */
const AuthRedirect = () => {
  const { user, isAuthenticated, isLoading, getRoleBasedRedirect } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  /** Dedup key so we re-run when active_profile_type changes (e.g. after refreshUser). */
  const lastRedirectKeyRef = useRef<string>('');

  useEffect(() => {
    console.log('🔄 [AUTH REDIRECT] Effect triggered:', {
      isLoading,
      isAuthenticated,
      hasUser: !!user,
      path: location.pathname,
    });

    // Only redirect if auth is loaded and user is authenticated
    if (!isLoading && isAuthenticated && user) {
      // If onboarding is incomplete, do not run role redirects here.
      // OnboardingGate will take over and route to /signup, and letting this
      // component redirect first causes a quick double-navigation "blink".
      if (needsSignupResume(user)) {
        return;
      }

      const currentPath = location.pathname;
      const currentRole = (user.role || '').toLowerCase();
      const explicitProfile = String(
        user.active_profile_type || '',
      ).toLowerCase();
      const activeProfile =
        explicitProfile || (currentRole === 'doctor' ? 'doctor' : 'patient');

      console.log('🔄 [AUTH REDIRECT] Checking redirect:', {
        currentPath,
        currentRole,
        activeProfile,
        lastKey: lastRedirectKeyRef.current,
      });

      const redirectKey = `${currentPath}|${currentRole}|${activeProfile}`;
      // Skip if we've already processed this exact path + role + active profile
      if (lastRedirectKeyRef.current === redirectKey) {
        console.log(
          '⏭️ [AUTH REDIRECT] Skipping-already processed this path/role',
        );
        return;
      }

      lastRedirectKeyRef.current = redirectKey;

      const expectedPath = getRoleBasedRedirect(
        user.role,
        user.active_profile_type,
      );
      console.log('🔄 [AUTH REDIRECT] Expected path:', expectedPath);

      // Check if user is on a valid route for their role (case-insensitive role check)
      const userRoleLower = currentRole;
      // Allow exact matches of /admin and /doctor (they redirect to dashboard via routes)
      // Also allow any path that starts with /admin/ or /doctor/ (with trailing slash)
      // This prevents false matches when checking for /doctor
      const isOnAdminRoute =
        userRoleLower === 'admin' &&
        (currentPath === '/admin' || currentPath.startsWith('/admin/'));
      const isOnDoctorRoute =
        userRoleLower === 'doctor' &&
        (currentPath === '/doctor' || currentPath.startsWith('/doctor/'));
      // Patient-shell routes: landing, browse, patient profile, etc.
      // Doctors in *doctor* session must not be treated as "home" on `/` or `/profile` —
      // otherwise they stay on the patient navbar forever (expectedPath is /doctor/dashboard).
      // Doctors in *patient* session (or browsing) still use these routes like normal users.
      const pathMatchesPatientShellArea =
        currentPath === '/' ||
        currentPath.startsWith('/remedy') ||
        currentPath.startsWith('/doctors') ||
        currentPath.startsWith('/liked') ||
        currentPath.startsWith('/tried') ||
        currentPath.startsWith('/saved') ||
        currentPath.startsWith('/collection') ||
        currentPath.startsWith('/search') ||
        currentPath.startsWith('/profile') ||
        currentPath.startsWith('/notifications') ||
        currentPath.startsWith('/family') ||
        currentPath.startsWith('/invite') ||
        currentPath.startsWith('/team') ||
        currentPath.startsWith('/approver-queue') ||
        currentPath.startsWith('/review-comments') ||
        currentPath.startsWith('/cook-assist') ||
        currentPath.startsWith('/dosha-test');

      const isHomeOrPatientProfile =
        currentPath === '/' || currentPath.startsWith('/profile');

      const doctorMayUsePatientShell =
        userRoleLower !== 'doctor' ||
        activeProfile === 'patient' ||
        (activeProfile === 'doctor' && !isHomeOrPatientProfile);

      const isOnUserRoute =
        (userRoleLower === '' ||
          userRoleLower === 'user' ||
          userRoleLower === 'doctor') &&
        pathMatchesPatientShellArea &&
        doctorMayUsePatientShell;

      // Don't redirect if already in a valid role area.
      // Authenticated users on /login (or forgot/reset password) MUST be sent
      // to their home — e.g. logged in in another tab, then refreshed this tab.
      const isOnGuestAuthPage =
        currentPath === '/login' ||
        currentPath === '/forgot-password' ||
        currentPath.startsWith('/reset-password');

      if (isOnGuestAuthPage) {
        console.log(
          '🔄 [AUTH REDIRECT] Authenticated on guest auth page — redirecting to',
          expectedPath,
        );
        navigate(expectedPath, { replace: true });
        return;
      }

      // NEVER redirect if user is already on a protected route for their role
      // This prevents redirecting away from dashboard when user data refreshes
      const shouldNotRedirect =
        isOnAdminRoute || // Admin on any /admin/* route-don't redirect
        isOnDoctorRoute || // Doctor on any /doctor/* route-don't redirect
        isOnUserRoute || // User on valid user routes-don't redirect
        currentPath === '/signup'; // Signup handled by OnboardingGate / needsSignupResume

      // Only redirect if user is on an invalid route for their role
      // This mainly catches: admin/doctor on landing page, or wrong role sections
      console.log('🔄 [AUTH REDIRECT] Decision:', {
        shouldNotRedirect,
        currentPath,
        expectedPath,
        isOnAdminRoute,
        isOnDoctorRoute,
        isOnUserRoute,
      });

      if (!shouldNotRedirect && currentPath !== expectedPath) {
        console.log(
          '🔄 [AUTH REDIRECT] REDIRECTING from',
          currentPath,
          'to',
          expectedPath,
          'for role',
          userRoleLower,
        );
        navigate(expectedPath, { replace: true });
      } else {
        console.log(
          '✅ [AUTH REDIRECT] No redirect needed-user is on valid route',
        );
      }
    } else if (!isLoading && !isAuthenticated) {
      console.log('🚪 [AUTH REDIRECT] User not authenticated, resetting refs');
      // Reset refs when user logs out
      lastRedirectKeyRef.current = '';
    } else {
      console.log('⏳ [AUTH REDIRECT] Waiting for auth to load...');
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    location.pathname,
    navigate,
    getRoleBasedRedirect,
  ]);

  return null; // This component doesn't render anything
};

export default AuthRedirect;
